import * as React from 'react';
import { Intent, Button, Tooltip, H4, Spinner, Icon, H3 } from '@blueprintjs/core';
import { LoadableSVG } from '../LoadableSVG';
import { CoachingAudio } from '../CoachingAudio';
import { executeQueryPostAuth, executeQueryGetAuth } from 'app/utils/api';
import { UserState } from 'app/reducers/state';
import { KryPlayDetails } from '../KryPlayDetails';
import TextAreaV2 from '../TextAreaV2';

interface Props {
    jouranlGraphic: string;
    coachingAudio: string;
    gameID: string;
    user: UserState;
    prompt: string;
    blockObjectives?: boolean;
    recieveJournal: (entry: string) => void;
    style: string;
    cachedDefaultJournal?: string;
};

interface State {
    isJournalVisible: boolean;
    hasAudioFinishedOnce: boolean;
    writtenJouranl: string;
    loadingObjectiveData: boolean;
    objectiveData: any;
    hasViewedObjectives: boolean;
    viewGraphic: boolean;
    hasViewedAllObjectives: boolean;
}

export class JournalWriter2 extends React.Component<Props, State> {

    state: State = {
        isJournalVisible: false,
        hasAudioFinishedOnce: false,
        writtenJouranl: '',
        loadingObjectiveData: true,
        hasViewedObjectives: false,
        hasViewedAllObjectives: false,
        viewGraphic: true,
        objectiveData: {}
    }

    componentDidMount() {
        executeQueryPostAuth("data/results/" + this.props.gameID, {}, this.props.user, (postResponse) => {
            executeQueryGetAuth("data/results/" + this.props.gameID, {}, this.props.user, (objectiveResponse) => {
                const response = JSON.parse(objectiveResponse.text);

                const {
                    blockObjectives
                } = this.props;

                // Mark objectives viewed if we're blocking them
                const hasViewedAllObjectives = blockObjectives === true;

                this.setState({
                    loadingObjectiveData: false,
                    objectiveData: response,
                    hasViewedAllObjectives,
                    writtenJouranl: this.getPrePlanTemplate()
                });
            });
        });
    }

    getPrePlanTemplate = () => {
        const directive     = 'Dominate the conversation by speaking more often, dismiss any non-priority topics and push your own priorities before ending the meeting quickly.';
        const delegative    = 'Listen and encourage Oli a lot, let him direct the conversation. Show Oli you are interested in what he has to say by asking and supporting what he brings up.';
        const participative = 'Share the dialog equally. Agree to something that Oli brings up but make sure you also get your priorities completed.';
        const explore       = 'Improve the relationship with Oli, calm him down, find common ground and use that altered leadership potential to focus him on a win-win outcome.';

        const {
            style
        } = this.props;

        if (style.indexOf('Directive Style') > 0)     return directive;
        if (style.indexOf('Delegative Style') > 0)    return delegative;
        if (style.indexOf('Participative Style') > 0) return participative;
        if (style.indexOf('Explore Play') > 0)        return explore;

        return directive;
    }

    renderGraphic = () => {
        const {
            blockObjectives
        } = this.props;

        const hideObjectiveView = blockObjectives === true;

        const {
            viewGraphic,
            objectiveData
        } = this.state;

        if (viewGraphic) {
            return <span style={{flexGrow: 1, flexBasis: 0}}>
                <LoadableSVG imgURL={this.props.jouranlGraphic} />
            </span>
        }

        if (hideObjectiveView) {
            return <span style={{flexGrow: 1, flexBasis: 0}}>
                <p style={{textAlign: 'center'}}>
                    <H3>This play has no objectives. Plan using your best judgement.</H3>
                </p>
            </span>
        }

        // Render the objectives
        return <span style={{flexGrow: 1, flexBasis: 0}}>
            <KryPlayDetails 
                hideScore={true}
                customWarningStyle={{right: '25px', top: '15px'}}
                user={this.props.user}
                selectedGame={objectiveData}
                canEditJournal={false}
                inSimulator={true}
                hasSeenAllCategories={() => {
                    this.setState({
                        hasViewedAllObjectives: true
                    });
                }}
                updateJournalDataInline={(stage: string, payload: string, gameID: string) => {
                    // ignore this, we aren't letting them edit in this mode
                }}
            />
        </span>
    }

    renderJournalWarning = () => {
        const {
            writtenJouranl,
            hasViewedObjectives,
            hasViewedAllObjectives
        } = this.state;

        const wordCount = writtenJouranl.split(' ').length;

        if (wordCount > 10) return '';

        if (hasViewedObjectives === false) {
            return 'You should view the objectives before committing to your plan'
        }

        if (hasViewedAllObjectives === false) {
            return 'You have not viewed all of the objective categories yet'
        }

        return 'You might want to write a more detailed plan.';
    }

    // renderBlankJournal = () => {
    //     return <span style={{flexShrink: 1, transition: 'all 2s'}}></span>
    // }

    renderJournal = () => {
        const defaultTextValue = this.props.cachedDefaultJournal ? this.props.cachedDefaultJournal : this.getPrePlanTemplate();

        return <span style={{flexShrink: 1, transition: 'all 2s'}}>
            <span style={{position: 'relative'}}>
                <H4>{this.props.prompt}</H4>
                <TextAreaV2 onChange={(event) => {
                    this.setState({
                        writtenJouranl: event.target.value
                    });
                }} defaultValue={defaultTextValue} className={"journalTextArea"} rows={15} style={{width: '100%', height: '180px'}}></TextAreaV2>
            </span>
        </span>
    }

    renderChevron = (active: boolean) => {
        if (active === false) {
            return <span style={{
                float: 'left',
                display: 'block',
                height: '10px',
                width: '48px'
            }}>
            </span>
        }

        return <span>
            <Icon iconSize={32} style={{marginRight: '8px', marginLeft: '8px', marginTop: '10px', float: 'left'}} icon='chevron-right' />
        </span>
    }

    renderGraphicButton = () => {
        return <span style={{display: 'block'}}>
            {this.renderChevron(this.state.viewGraphic)}
            <Button onClick={() => {this.setState({viewGraphic: true})}} large style={{display: 'block', width: '350px', height: '50px', marginTop: '30px', marginLeft: '20px'}} intent={Intent.PRIMARY}>Review the Lesson</Button>
        </span>
    }

    renderObjectivesButton = () => {
        return <span style={{display: 'block'}}>
            {this.renderChevron(this.state.viewGraphic === false)}
            <Button onClick={() => {this.setState({viewGraphic: false, hasViewedObjectives: true})}} large style={{display: 'block', width: '350px', marginTop: '10px', height: '50px', marginLeft: '20px'}} intent={Intent.PRIMARY}>Plan using Objectives</Button>
        </span>
    }

    renderSaveButton = () => {
        const {
            writtenJouranl
        } = this.state;

        const warningMessage = this.renderJournalWarning();
        let shouldBeEnabled = writtenJouranl !== '';

        const saveButton = <Button disabled={!shouldBeEnabled} large style={{display: 'block', width: '350px', marginTop: '10px', height: '60px', marginLeft: '50px'}} onClick={() => {
            this.props.recieveJournal(this.state.writtenJouranl);
        }} intent={Intent.SUCCESS}>Save & Execute your Plan</Button>

        if (warningMessage === undefined) {
            return saveButton;
        }

        return <Tooltip content={warningMessage}>
            {saveButton}
        </Tooltip>
    }

    renderAudioComponent = () => {
        return <CoachingAudio 
            audioSource={this.props.coachingAudio}
            donePlaying={() => {
                this.setState({
                    hasAudioFinishedOnce: true
                });
            }}
        />
    }

    renderDisplayRegion = () => {
        return this.renderGraphic();
    }

    renderControlRegion = () => {
        return <span style={{flexGrow: 0}}>
            <span style={{display: 'flex', flexDirection: 'row'}}>
                <span style={{flexGrow: 1}}>    
                    {this.renderJournal()}
                </span>
                <span style={{width: '400px'}}>
                    {this.renderGraphicButton()}
                    {/* {this.renderObjectivesButton()} */}
                    {this.renderSaveButton()}
                </span>
            </span>
        </span>
    }

	render() {
        const {
            loadingObjectiveData
        } = this.state;

        if (loadingObjectiveData) return <span style={{display: 'block', width: '100%', margin: 'auto', marginTop: '40px'}}>
            <Spinner />
        </span>

        return <div className="journalParent" style={{ width: '100%'}}>
            {this.renderAudioComponent()}
            <span style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
                {this.renderDisplayRegion()}
                {this.renderControlRegion()}
            </span>
        </div>
    }
}
